// Events of inventory, purchasing and planning [02 §5.1; 05 §17; 07 §10.2 P01/P02 and movement-based rules].
import { z } from 'zod';

import { businessDateSchema, idSchema } from '../kernel/index.ts';
import {
  currencyCodeSchema,
  moneySchema,
  unitRateSchema,
  fxRateDecimalSchema,
} from '../units/index.ts';
import { documentRefSchema } from '../dto/common.ts';
import { qtyDtoSchema, utcTimestampSchema } from '../dto/scalars.ts';
import { ETA_MILESTONE, ETA_SOURCE, MOVEMENT_TYPE } from '../enums/index.ts';
import { defineEvent } from './define.ts';

/**
 * Valued movements of one ledger write. Consumers: posting (valuation → GL via account mapping,
 * 05 §17; 07 P08–P11, P14, P17–P20, P28) and D2C stock sync (07 BR-D2C-010).
 */
export const inventoryMovementPostedV1 = defineEvent({
  type: 'inventory.movement.posted',
  version: 1,
  aggregateType: 'stock_movement',
  specRefs: ['02 §5.1', '05 §17'],
  payload: z.object({
    reference: documentRefSchema,
    businessDate: businessDateSchema,
    movements: z
      .array(
        z.object({
          movementId: idSchema,
          movementType: MOVEMENT_TYPE.schema,
          reasonCode: z.string().min(1).nullable(),
          itemId: idSchema,
          warehouseId: idSchema,
          lotId: idSchema.nullable(),
          rollId: idSchema.nullable(),
          qty: qtyDtoSchema,
          unitCost: unitRateSchema,
          value: moneySchema,
        }),
      )
      .min(1),
  }),
});

export const purchasingPoIssuedV1 = defineEvent({
  type: 'purchasing.po.issued',
  version: 1,
  aggregateType: 'purchase_order',
  specRefs: ['05 §17'],
  payload: z.object({
    purchaseOrderId: idSchema,
    number: z.string().min(1),
    supplierId: idSchema,
    revisionNo: z.int().nonnegative(),
    total: moneySchema,
  }),
});

export const purchasingPoRevisedV1 = defineEvent({
  type: 'purchasing.po.revised',
  version: 1,
  aggregateType: 'purchase_order',
  specRefs: ['05 §17'],
  payload: z.object({
    purchaseOrderId: idSchema,
    number: z.string().min(1),
    revisionNo: z.int().positive(),
    reason: z.string().min(1),
    total: moneySchema,
  }),
});

export const purchasingPoClosedV1 = defineEvent({
  type: 'purchasing.po.closed',
  version: 1,
  aggregateType: 'purchase_order',
  specRefs: ['05 §17'],
  payload: z.object({
    purchaseOrderId: idSchema,
    number: z.string().min(1),
    closedAs: z.enum(['closed', 'cancelled']),
    closedOn: businessDateSchema,
  }),
});

const receiptLine = z.object({
  receiptLineId: idSchema,
  purchaseOrderLineId: idSchema,
  itemId: idSchema,
  lotId: idSchema.nullable(),
  qty: qtyDtoSchema,
  unitPrice: unitRateSchema,
  baseValue: moneySchema,
});

/** P01 inv.receipt: qty × PO price × receipt-date rate (GRNI); also feeds AP 3-way match. */
export const purchasingGoodsReceiptPostedV1 = defineEvent({
  type: 'purchasing.goods_receipt.posted',
  version: 1,
  aggregateType: 'goods_receipt',
  specRefs: ['05 §17', '02 §5.1 purchasing.receipt.posted', '07 §10.2 P01'],
  payload: z.object({
    goodsReceiptId: idSchema,
    number: z.string().min(1),
    supplierId: idSchema,
    purchaseOrderId: idSchema,
    receivedOn: businessDateSchema,
    currency: currencyCodeSchema,
    fxRate: fxRateDecimalSchema,
    lines: z.array(receiptLine).min(1),
  }),
});

/**
 * v2 (CR-W2b-12, R-EVT-1): adds `postedOn`, the ledger date the stock was booked on (BR-RCV-009: the received date
 * may fall in a period locked for inventory, then the receipt posts on the first open date). AP / GRNI post on
 * `postedOn`; every v1 field is kept, so consumers reading `receivedOn` work on both versions.
 */
export const purchasingGoodsReceiptPostedV2 = defineEvent({
  type: 'purchasing.goods_receipt.posted',
  version: 2,
  aggregateType: 'goods_receipt',
  specRefs: ['05 §17', '05 BR-RCV-009', '07 §10.2 P01'],
  payload: z.object({
    goodsReceiptId: idSchema,
    number: z.string().min(1),
    supplierId: idSchema,
    purchaseOrderId: idSchema,
    receivedOn: businessDateSchema,
    postedOn: businessDateSchema,
    currency: currencyCodeSchema,
    fxRate: fxRateDecimalSchema,
    lines: z.array(receiptLine).min(1),
  }),
});

/** P02 receipt reversal (negative P01). */
export const purchasingGoodsReceiptReversedV1 = defineEvent({
  type: 'purchasing.goods_receipt.reversed',
  version: 1,
  aggregateType: 'goods_receipt',
  specRefs: ['05 §17', '07 §10.2 P02'],
  payload: z.object({
    goodsReceiptId: idSchema,
    number: z.string().min(1),
    supplierId: idSchema,
    purchaseOrderId: idSchema,
    reversedOn: businessDateSchema,
    reason: z.string().min(1),
    currency: currencyCodeSchema,
    fxRate: fxRateDecimalSchema,
    lines: z.array(receiptLine).min(1),
  }),
});

export const purchasingEtaChangedV1 = defineEvent({
  type: 'purchasing.eta.changed',
  version: 1,
  aggregateType: 'inbound_shipment',
  specRefs: ['05 §17'],
  payload: z.object({
    inboundShipmentId: idSchema,
    purchaseOrderId: idSchema.nullable(),
    milestone: ETA_MILESTONE.schema,
    previousDate: businessDateSchema.nullable(),
    newDate: businessDateSchema.nullable(),
    source: ETA_SOURCE.schema,
  }),
});

export const planningRunCompletedV1 = defineEvent({
  type: 'planning.run.completed',
  version: 1,
  aggregateType: 'requirement_run',
  specRefs: ['05 §17'],
  payload: z.object({
    runId: idSchema,
    startedAt: utcTimestampSchema,
    completedAt: utcTimestampSchema,
    horizonWeeks: z.int().positive(),
    raisePoCount: z.int().nonnegative(),
    expediteCount: z.int().nonnegative(),
  }),
});

export const SUPPLY_EVENTS = [
  inventoryMovementPostedV1,
  purchasingPoIssuedV1,
  purchasingPoRevisedV1,
  purchasingPoClosedV1,
  purchasingGoodsReceiptPostedV1,
  purchasingGoodsReceiptPostedV2,
  purchasingGoodsReceiptReversedV1,
  purchasingEtaChangedV1,
  planningRunCompletedV1,
] as const;
